import { ArrowRightIcon, MoreVerticalIcon, PencilIcon, ScissorsIcon, Trash2Icon } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { fmtDate } from "./ShipmentDetailUtils"
import { TRIP_STATUS_LABELS, TRIP_STATUS_COLORS, type TripRow, type TripStatus } from "./ShipmentDetailTypes"
import { travelAdvanceLabel, travelAdvanceColor, travelAdvanceHover, nextTravelAdvance, type TravelAdvance } from "@/lib/travel-advance"

import { type BillingStatus, BILLING_STATUS_LABELS, BILLING_STATUS_COLORS } from "@/lib/billing-status"

interface TripActions {
  onEdit?:          (t: TripRow) => void
  onSplit?:         (t: TripRow) => void
  onDelete?:        (t: TripRow) => void
  onToggleAdvance?: (t: TripRow, next: TravelAdvance) => void
}

interface ShipmentTripsTableProps {
  trips:    TripRow[]
  actions?: TripActions   // omit for read-only view
}

export function ShipmentTripsTable({ trips, actions }: ShipmentTripsTableProps) {
  const hasActions = !!(actions?.onEdit || actions?.onSplit || actions?.onDelete)

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-14"># Viaje</TableHead>
            <TableHead className="w-16">Móvil</TableHead>
            <TableHead>Camión / Semi</TableHead>
            <TableHead>Chofer</TableHead>
            <TableHead>Origen → Destino</TableHead>
            <TableHead className="w-14 text-right">KM</TableHead>
            <TableHead className="w-36">Salida / Llegada</TableHead>
            <TableHead className="w-28">Estado</TableHead>
            <TableHead className="w-12 text-center">Ant.</TableHead>
            {hasActions && <TableHead className="w-16" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {trips.map((t) => (
            <TableRow key={t.id}>
              {/* # Viaje */}
              <TableCell>
                <span className="font-mono text-xs font-semibold text-primary">#{t.internalId}</span>
              </TableCell>

              {/* Móvil */}
              <TableCell>
                {t.truckUnitNumber != null ? (
                  <span className="font-mono text-xs font-semibold">M{t.truckUnitNumber}</span>
                ) : (
                  <span className="text-muted-foreground/30 text-xs">—</span>
                )}
              </TableCell>

              {/* Camión / Semi */}
              <TableCell>
                {t.truckLicensePlate ? (
                  <div className="flex items-center gap-1.5 text-sm font-medium leading-tight">
                    <span className="font-mono">{t.truckLicensePlate}</span>
                  </div>
                ) : null}
                {(t.trailerLicensePlate || t.trailerUnitNumber != null) ? (
                  <div className="flex items-center gap-1.5 text-sm leading-tight text-muted-foreground">
                    {t.trailerUnitNumber != null && (
                      <span className="font-mono text-[10px] bg-muted/60 px-1 py-0.5 rounded">
                        S{t.trailerUnitNumber}
                      </span>
                    )}
                    {t.trailerLicensePlate && (
                      <span className="font-mono text-xs">{t.trailerLicensePlate}</span>
                    )}
                  </div>
                ) : null}
                {!t.truckLicensePlate && !t.trailerLicensePlate && (
                  <span className="text-muted-foreground/30 text-xs">—</span>
                )}
              </TableCell>

              {/* Chofer */}
              <TableCell>
                {t.driverName ? (
                  <div className="flex items-center gap-1.5">
                    <Avatar className="size-5">
                      <AvatarFallback
                        className="text-white text-[9px] font-semibold"
                        style={{ backgroundColor: avatarColor(t.driverName) }}
                      >
                        {avatarInitials(t.driverName)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-muted-foreground">{t.driverName}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground/30 text-xs">—</span>
                )}
              </TableCell>

              {/* Origen → Destino */}
              <TableCell>
                {(t.originCityName || t.destinationCityName) ? (
                  <div className="flex items-center gap-1 text-sm">
                    <span>{t.originCityName ?? "—"}</span>
                    <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground" />
                    <span>{t.destinationCityName ?? "—"}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground/30 text-xs">—</span>
                )}
              </TableCell>

              {/* KM */}
              <TableCell className="text-right">
                {t.routeKm != null ? (
                  <span className="text-sm tabular-nums">{t.routeKm}</span>
                ) : (
                  <span className="text-muted-foreground/30 text-xs">—</span>
                )}
              </TableCell>

              {/* Salida / Llegada — stacked like TripsPage */}
              <TableCell className="text-xs text-muted-foreground tabular-nums">
                <div className="flex flex-col gap-0.5 leading-tight">
                  <span>{fmtDate(t.departureAt)}</span>
                  <span className="text-muted-foreground/60">{fmtDate(t.arrivalAt)}</span>
                </div>
              </TableCell>

              {/* Estado + Facturación apilados */}
              <TableCell>
                <div className="flex flex-col gap-1">
                  <span className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                    TRIP_STATUS_COLORS[t.status as TripStatus] ?? "bg-muted text-muted-foreground",
                  )}>
                    {TRIP_STATUS_LABELS[t.status as TripStatus] ?? t.status}
                  </span>
                  <span className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                    BILLING_STATUS_COLORS[t.billingStatus ?? "pendiente"],
                  )}>
                    {BILLING_STATUS_LABELS[t.billingStatus ?? "pendiente"]}
                  </span>
                </div>
              </TableCell>

              {/* Anticipo */}
              <TableCell className="text-center">
                {actions?.onToggleAdvance ? (
                  <button
                    type="button"
                    onClick={() => actions.onToggleAdvance!(t, nextTravelAdvance(t.hasTravelAdvance))}
                    title="Click para cambiar"
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors cursor-pointer",
                      travelAdvanceColor(t.hasTravelAdvance),
                      travelAdvanceHover(t.hasTravelAdvance),
                    )}
                  >
                    {travelAdvanceLabel(t.hasTravelAdvance)}
                  </button>
                ) : (
                  <span className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                    travelAdvanceColor(t.hasTravelAdvance),
                  )}>
                    {travelAdvanceLabel(t.hasTravelAdvance)}
                  </span>
                )}
              </TableCell>

              {/* Acciones — botón Split inline + DropdownMenu */}
              {hasActions && (
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {actions?.onSplit && (
                      <button
                        type="button"
                        title="Dividir viaje"
                        onClick={() => actions.onSplit!(t)}
                        className="flex items-center justify-center size-7 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <ScissorsIcon className="size-3.5" />
                      </button>
                    )}
                    {(actions?.onEdit || actions?.onDelete) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger render={
                          <button className="flex items-center justify-center size-7 rounded hover:bg-muted transition-colors">
                            <MoreVerticalIcon className="size-3.5 text-muted-foreground" />
                          </button>
                        } />
                        <DropdownMenuContent align="end" className="w-40">
                          {actions?.onEdit && (
                            <DropdownMenuItem onClick={() => actions.onEdit!(t)}>
                              <PencilIcon className="size-3.5" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          {actions?.onDelete && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => actions.onDelete!(t)}
                              >
                                <Trash2Icon className="size-3.5" />
                                Eliminar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
